// Справка „Монитори" (Справки → kitchen-monitors).
//
// Един ред = един продуктов ред или добавка, минал(а) през кухненския/барския
// монитор: кога е влязъл, колко е чакал до започване, колко се е готвил, кога
// е издаден и сервиран — и кой е натиснал всеки от преходите. Числата идват от
// одита на статусите, а `GET /reports/analytics/kitchen-monitors` връща целия
// период наведнъж, затова таблицата работи в КЛИЕНТСКИ режим.
//
// БЕЗ „Δ спрямо предходния период": endpoint-ът връща обобщение само за
// поискания отрязък и екранът никога не е сравнявал два периода — бутон, който
// не мени нищо, е по-лош от липсващ бутон.

import { useMemo, useState } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useKitchenMonitorReport } from "../../../hooks/useReports";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { useOrderedItemKindKeys } from "../useOrderedItemKindKeys";
import { DataTable } from "../../../components/dataTable";
import type { KitchenMonitorReportRow } from "../../../api/reportsKitchenMonitors";
import ExportExcelButton from "./ExportExcelButton";
import { ReportTopBar } from "./ReportTopBar";
import { pluralLabel } from "./reportPlural";
import { KitchenMonitorsKpis } from "./kitchenMonitors/KitchenMonitorsKpis";
import { KitchenMonitorMobileCard } from "./kitchenMonitors/KitchenMonitorMobileCard";
import { useKitchenMonitorsColumns } from "./kitchenMonitors/kitchenMonitorsColumns";
import { exportKitchenMonitors } from "./kitchenMonitors/kitchenMonitorsExport";
import { summarizeKitchenMonitorRows } from "./kitchenMonitors/kitchenMonitorsMath";
import {
  businessDayEndFrom,
  businessDayStartFrom,
} from "../../../lib/businessDay";

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

export const KitchenMonitorsReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;

  // „Тип" на реда: Рецепта/Асортимент според режима със склад, или Добавка —
  // същата терминология като справката „Асортименти"/„Рецепти".
  const { addonKey, sellableKey } = useOrderedItemKindKeys();

  // Датата живее и в query параметъра ?date= — пази се при смяна на справка.
  const [dateRange, setDateRange] = useReportsDateParam(() => [
    businessDayStartFrom(dayjs()),
    businessDayEndFrom(dayjs()),
  ], "business");
  const [searchText, setSearchText] = useState("");
  const [visibleRows, setVisibleRows] = useState<KitchenMonitorReportRow[]>([]);

  const filters = useMemo(() => {
    const params: Record<string, string> = {};
    if (dateRange?.[0] && dateRange?.[1]) {
      params.start_date = dateRange[0].format("YYYY-MM-DD HH:mm:ss");
      params.end_date = dateRange[1].format("YYYY-MM-DD HH:mm:ss");
    }
    return params;
  }, [dateRange]);

  const { data, isLoading, isFetching, refetch } =
    useKitchenMonitorReport(filters);
  const rows = useMemo(() => data?.rows ?? [], [data]);

  // Търсачката гледа всичко, което човек чете в реда: маса, артикул, станция
  // и четиримата оператори. Станцията и операторите имат и собствени филтри в
  // колоните — търсачката е за „намери ми онзи ред", не за стесняване.
  const searchedRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      [
        row.bill_name,
        row.item_name,
        row.station_name,
        row.started_by ?? "",
        row.cooked_by ?? "",
        row.issued_by ?? "",
        row.served_by ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [rows, searchText]);

  // Кутийките следват ТЪРСАЧКАТА, както таблицата и Excel — `summary` от
  // отговора важи за целия период и при търсене показваше средно време на
  // редове, които не са на екрана (същото решение като в „По станции").
  const summary = useMemo(
    () => summarizeKitchenMonitorRows(searchedRows),
    [searchedRows],
  );

  const columns = useKitchenMonitorsColumns({ addonKey, sellableKey });

  const handleExport = () =>
    exportKitchenMonitors({
      rows: visibleRows,
      addonKey,
      sellableKey,
      t,
    });

  const tableTitle = (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {t("reportsPage.kmListKicker")}
      </span>
      <span className="text-[16px] font-extrabold leading-none text-ink">
        {pluralLabel(
          t,
          visibleRows.length,
          "reportsPage.kmResultOne",
          "reportsPage.kmResults",
        )}
        <span className="font-medium text-muted">
          {` · ${t("reportsPage.kmTableHint")}`}
        </span>
      </span>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.kitchenMonitors")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              dayMode="business"
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
              withTime
            />
          </div>

          {isLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <KitchenMonitorsKpis summary={summary} isNarrow={narrow} />

              <DataTable<KitchenMonitorReportRow>
                tableId="kitchen-monitors"
                data={searchedRows}
                columns={columns}
                getRowId={(row) =>
                  `${row.bill_element_item_id}-${row.is_addition}`
                }
                refreshing={isFetching}
                ariaLabel={t("reportsPage.kitchenMonitors")}
                title={tableTitle}
                search={{
                  value: searchText,
                  onChange: setSearchText,
                  placeholder: t("reportsPage.kmSearchPlaceholder"),
                }}
                onFilteredRowsChange={setVisibleRows}
                toolbarRight={
                  <ExportExcelButton
                    onExport={handleExport}
                    disabled={visibleRows.length === 0}
                    bare
                  />
                }
                mobileCard={(row) => (
                  <KitchenMonitorMobileCard
                    row={row}
                    addonKey={addonKey}
                    sellableKey={sellableKey}
                  />
                )}
                // Най-новото горе — това е и редът, в който BE-то връща
                // редовете; изрично, за да свети стрелката и човек да види
                // защо списъкът е подреден така.
                initialSorting={[{ id: "ordered_at", desc: true }]}
                emptyTitle={t("reportsPage.kmNoData")}
                emptyHint={t("reportsPage.kmEmptyHint")}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
